import { goto as _goto } from "$app/navigation";
import type { Page } from "@sveltejs/kit";

let goto = _goto;

//####################################################################################################
// URLSearchParams Helpers (Functional Approach)
//####################################################################################################

export type queryModAction = (url: URLSearchParams) => void;

/**
 * Modify a URL's query parameters using any of the provided actions. See
 * {@link QueryMod}.
 * @param url Any URL object or query string.
 * @param actions Any modifier(s) with the same signarure as those returned
 * by {@link QueryMod} modifiers.
 * @returns An updated URLSearchParams object.
 */
export function queryMod(
	url: URL | URLSearchParams | string,
	...actions: queryModAction[]
): URLSearchParams {
	const sp = toSearchParams(url);
	for (const action of actions) {
		if (typeof action === 'function') action(sp);
	}
	return sp;
}

//####################################################################################################
// Query modifiers (Functional Approach cont)
//####################################################################################################

export const QueryMod = { //: Record<ActionName, queryModSingleAction | queryModMultiActions>
	/**
	 * Set a query parameter to a given value.
	 * @param param The parameter name to set.
	 * @param value The value to set the parameter to.
	 * @returns A query modifier.
	 */
	set: (param: string, value: string):queryModAction => (url: URLSearchParams) => {
		url.set(param, value);
	},

	/**
	 * Set a query parameter to a given value if it does not exist or exists
	 * with a different value. If `strictEqual` is false the parameter is
	 * removed if it exists with any value.
	 * @param param The parameter name to toggle.
	 * @param value The value to set if the parameter does not exist.
	 * @param existenceOnly If true then only the existence of the parameter
	 * is checked and the value's equality is ignored.
	 * @returns A query modifier.
	 */
	toggle: (param: string, value: string, existenceOnly = false):queryModAction => (url: URLSearchParams) => {
		if (url.has(param) && (existenceOnly || url.get(param) === value)) {
			url.delete(param);
		} else {
			url.set(param, value);
		}
	},

	/**
	 * Like `toggle` but the parameter is treated as a list of values and the
	 * toggle is performed on the list of values. If the value is not found
	 * then it is added to the list. If the value is found then it is removed
	 * from the list. If the list is empty then the parameter is removed.
	 * @param param The parameter name to toggle.
	 * @param value The value to add/remove from the list.
	 * @returns A query modifier.
	 */
	subtoggle: (param: string, value?: string):queryModAction => (url: URLSearchParams) => {
		if (typeof value === 'undefined') return;
		const list = url.get(param)?.split(',');
		if (list?.includes(value)) {
			const idx = list.findIndex(t => t === value);
			list.splice(idx, 1);
			if (list.length === 0) {
				url.delete(param);
			} else {
				url.set(param, list.join(','));
			}
		} else if (list) {
			list?.push(value);
			url.set(param, list.join(','));
		} else {
			url.set(param, value);
		}
	},

	/**
	 * Remove all the specified query parameters.
	 * @param params The parameter names to remove.
	 * @returns A query modifier.
	 */
	remove: (...params: string[]):queryModAction => (url: URLSearchParams) => {
		const remove = [] as string[];
		url.forEach((value, key) => params.includes(key) && remove.push(key));
		remove.forEach((param) => url.delete(param));
	},

	/**
	 * Keep only the specified query parameters.
	 * @param params The parameter names to keep.
	 * @returns A query modifier.
	 */
	keep: (...params: string[]):queryModAction => (url: URLSearchParams) => {
		const remove = [] as string[];
		url.forEach((value, key) => !params.includes(key) && remove.push(key));
		remove.forEach((param) => url.delete(param));
	}
}

//####################################################################################################
// URLSearchParams Helpers (OOP Approach making use of functions above)
//####################################################################################################

export function query(
	url: URL | URLSearchParams | string
) {
	return new Query(url);
}

export function queryFrom(
	object: ConstructorParameters<typeof URLSearchParams>[0],
) {
	return new Query(new URLSearchParams(object));
}

export class Query {

	private _sp: URLSearchParams;

	constructor(url: URL | URLSearchParams | string) {
		this._sp = toSearchParams(url);
	}

	/**
	 * 
	 * @param param Param name to set.
	 * @param value `undefined` will result in an empty string; `null` to remove
	 * @returns 
	 */
	set(param: string, value?: string | number | boolean | null):this {
		if (value === null) {
			QueryMod.remove(param)(this._sp);
		} else {
			QueryMod.set(param, value?.toString() ?? '')(this._sp);
		}
		return this;
	}

	setBulk(params?: Record<string, unknown> | [string, unknown][] | null):this {
		if (typeof params === 'undefined' || params === null) return this;
		if (Array.isArray(params)) params = Object.fromEntries(params);
		for (const [param, value] of Object.entries(params)) {
			if (typeof value !== undefined) this.set(param, value?.toString());
		}
		return this;
	}

	toggle(param: string, value: undefined | string | number | boolean, existenceOnly = false):this {
		if (typeof value !== 'undefined') {
			QueryMod.toggle(param, value.toString(), existenceOnly)(this._sp);
		}
		return this;
	}

	subtoggle(param: string, value?: string | number | boolean):this {
		QueryMod.subtoggle(param, value?.toString() ?? '')(this._sp);
		return this;
	}

	remove(...params: string[]):this {
		QueryMod.remove(...params)(this._sp);
		return this;
	}

	keep(...params: string[]):this {
		QueryMod.keep(...params)(this._sp);
		return this;
	}

	go(pathopt1?:string | Parameters<typeof goto>[1], pathopt2?: Parameters<typeof goto>[1]) {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return navto(this._sp, pathopt1, pathopt2);
	}

	/**
	 * Returns the value of a query parameter. `null` if the parameter does not exist.
	 * @param params A single parameter name.
	 */
	get(params: string):string;

	/**
	 * Returns an array of values for each query parameter. A `null` is returned for each
	 * parameter that does not exist.
	 * @param params An array of parameter names.
	 */
	get(params: string[]):string[];

	get(params: string[] | string) {
		if (Array.isArray(params)) return params.map((param) => this._sp.get(param));
		return this._sp.get(params);
	}

	toString():string {
		return this._sp.toString();
	}

	forEach(callbackfn: (value: string, key: string, parent: URLSearchParams) => void):void {
		this._sp.forEach(callbackfn);
	}

	toUrl(pathname: string | URL = ''):string {
		const path = typeof pathname === 'string' ? pathname : (pathname as URL).pathname ?? '';
		return `${path}?${this._sp.toString()}`;
	}

	// TODO: add tests
	toObject():Record<string, string> {
		return Object.fromEntries(this._sp.entries());
	}

	has(param: string):boolean {
		return this._sp.has(param);
	}

	hasAny(...params: string[]):boolean {
		return params.some((param) => this._sp.has(param));
	}

	hasAll(...params: string[]):boolean {
		return params.every((param) => this._sp.has(param));
	}

	hasValue(param: string, value: string | number | boolean | null = null):boolean {
		const val = this._sp.get(param);
		if (typeof value === 'undefined' || value === null) return typeof val === 'undefined' || value === null || val === 'null';
		return this._sp.get(param) === value.toString();
	}

	hasValueInList(param: string, value: string | number | boolean):boolean {
		const list = this._sp.get(param)?.split(',');
		return list?.includes(value?.toString()) ?? false;
	}

	hasValueInListAny(param: string, ...value: (string | number | boolean)[]):boolean {
		const list = this._sp.get(param)?.split(',');
		return value.map(v => v.toString()).some((v) => list?.includes(v)) ?? false;
	}

	hasValueInListAll(param: string, ...value: (string | number | boolean)[]):boolean {
		const list = this._sp.get(param)?.split(',');
		return value.map(v => v.toString()).every((v) => list?.includes(v)) ?? false;
	}

	diffParams(target: URL | URLSearchParams | string) {
		const targetSp = toSearchParams(target);
		const thisKeys = Array.from(this._sp.keys());
		const targetKeys = Array.from(targetSp.keys());

		return {
			commonKeys: thisKeys.filter((key) => targetKeys.includes(key)),
			onlyInThis: thisKeys.filter((key) => !targetKeys.includes(key)),
			onlyInTarget: targetKeys.filter((key) => !thisKeys.includes(key)),
		}
	}

}


//####################################################################################################
// URL Helpers
//####################################################################################################

/**
 * A wrapper for {@link queryMod} that returns a URL object instead. The URL
 * includes a pathname that can be specified.
 * @param url Any URL object or query string.
 * @param pathname A destination path to replace the current page pathname.
 * @param actions Any modifier(s) with the same signarure as those returned
 * by {@link QueryMod} modifiers.
 * @returns An updated URLSearchParams object.
 */
export function urlMod(
	url: URL | URLSearchParams | string,
	pathname: string,
	...actions: queryModAction[]
): URL {
	const sp = queryMod(url, ...actions);
	return new URL(`${pathname}?${sp.toString()}`, url instanceof URL ? url.origin : window.location.origin);
}


//####################################################################################################
// `navto` and overloads
//####################################################################################################

/**
 * `goto` with query parameter modifier(s).
 * @param page The current page state.
 * @param actions Query modifiers to apply to the page url search parameters.
 */
export function navto(
	page:Page | URL | URLSearchParams | string,
	...actions: queryModAction[]
):ReturnType<typeof goto>;

/**
 * `goto` with query parameter modifier(s).
 * @param page The current page state.
 * @param pathname A destination path to replace the current page pathname.
 * @param actions Query modifiers to apply to the page url search parameters.
 */
export function navto(
	page:Page | URL | URLSearchParams | string,
	pathname: string,
	...actions: queryModAction[]
):ReturnType<typeof goto>;

/**
 * `goto` with query parameter modifier(s).
 * @param page The current page state.
 * @param options The options for `goto`.
 * @param actions Query modifiers to apply to the page url search parameters.
 */
export function navto(
	page:Page | URL | URLSearchParams | string,
	options: Parameters<typeof goto>[1],
	...actions: queryModAction[]
):ReturnType<typeof goto>;

/**
 * `goto` with query parameter modifier(s).
 * @param page The current page state.
 * @param pathname A destination path to replace the current page pathname.
 * @param options The options for `goto`.
 * @param actions Query modifiers to apply to the page url search parameters.
 */
export function navto(
	page:Page | URL | URLSearchParams | string,
	pathname?: string,
	options?: Parameters<typeof goto>[1],
	...actions: queryModAction[]
):ReturnType<typeof goto>;

export function navto(
	page: Page | URL | URLSearchParams | string,
	pathopt1?: string | Parameters<typeof goto>[1] | queryModAction,
	pathopt2?: Parameters<typeof goto>[1] | queryModAction,
	...actions: queryModAction[]
) {
	if (typeof actions === 'undefined') actions = [];
	if (Array.isArray(pathopt2)) actions = pathopt2.concat(actions);
	if (Array.isArray(pathopt1)) actions = pathopt1.concat(actions);
	const originalPathname = (page as Page).url?.pathname ?? (page as URL).pathname ?? '';
	const pathname = (typeof pathopt1 === 'string') ? pathopt1 : originalPathname;
	let options = (typeof pathopt1 === 'object') ? pathopt1 : (typeof pathopt2 === 'object') ? pathopt2 : undefined;
	const sp = queryMod((page as Page).url ?? page, ...actions);
	if(!pathname) { //if we are not setting a pathname then avoid the scroll reset
		if (typeof options === 'undefined') options = {};
		// if (!('noScroll' in options)) options.noScroll = true; // this is going to be to confusing for devs
	}
	return goto(`${pathname}?${sp.toString()}`, options);
}

//####################################################################################################
// Query String Helpers
//####################################################################################################

export function hasParam(url: URL | URLSearchParams | string, param: string):boolean {
	const sp = toSearchParams(url);
	return sp.has(param);
}

export function hasParamValue(url: URL | URLSearchParams | string, param: string, value: string):boolean {
	const sp = toSearchParams(url);
	return sp.get(param) === value;
}

export function toSearchParams(url: URL | URLSearchParams | string):URLSearchParams {
	const split = (s:string) => {
		s = s.includes('?') ? s.split('?')[1] : s;
		s = s.includes('#') ? s.split('#')[0] : s;
		return s;
	}
	const str = (typeof url === 'string') ? split(url) : (url instanceof URL) ? url.searchParams.toString() : url.toString();
	return new URLSearchParams(str);
}

//####################################################################################################
// TESTING HELPERS
//####################################################################################################

export function mockGo(mock: typeof _goto) {
	goto = mock;
}
